import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService, ResolveParams } from '@/services/notificationService';
import { Notification } from '@/types/notification';
import { toastService } from '@/services/toastService';

interface UseDeeplinkResult {
  navigateToDeeplink: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  resetError: () => void;
}

export function useDeeplink(notification: Notification | null): UseDeeplinkResult {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetError = useCallback(() => setError(null), []);

  const navigateToDeeplink = useCallback(async () => {
    if (!notification) {
      console.error('[useDeeplink] No notification provided');
      setError('Notificação indisponível');
      toastService.showDeeplinkError('not_found');
      return;
    }

    setIsLoading(true);
    setError(null);

    const payload: ResolveParams = {
      project_id: notification.project_id ?? null,
      resource_type: notification.resource_type ?? null,
      resource_id: notification.resource_id ?? null,
    };

    try {
      const result = await notificationService.resolveDeeplink(payload);

      if (!result.is_accessible) {
        const reason = (result.error_code as any) || 'not_found';
        console.error('[useDeeplink] Access denied or resource not found:', reason);
        toastService.showDeeplinkError(reason);
        // Don't set error state for obsolete resources - just show toast
        if (reason !== 'obsolete') {
          setError(result.error_message || 'Não foi possível abrir a notificação');
        }
        return;
      }

      const path = result.path || '/projects';
      navigate(path);
    } catch (err: any) {
      console.error('[useDeeplink] Error caught:', err);
      const status = err?.status || err?.response?.status;
      if (status === 403) {
        toastService.showDeeplinkError('permission_denied');
      } else if (status === 404) {
        toastService.showDeeplinkError('not_found');
      } else if (status === 410) {
        toastService.showDeeplinkError('obsolete');
        // Don't set error state for obsolete - just show toast and visual indicator
      } else {
        toastService.showDeeplinkError('offline');
      }
      // Only set error state for non-obsolete errors
      if (status !== 410) {
        setError(err?.message || 'Erro ao resolver deeplink');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, notification]);

  return { navigateToDeeplink, isLoading, error, resetError };
}
